// Subtasks (DEC-0452): a subtask is a FULL task instance, linked to a parent
// task, one level deep. It has its own status, priority, effort and impact, and
// it appears in the tasks library like any other task.
//
// The link is a note_relations row with relation_type 'subtask_of'
// (source = the subtask, target = the parent), the same table that already holds
// the blocked_by arcs. No new table.
//
// One level only: a subtask cannot have subtasks, and a task that already has
// subtasks cannot become one. That keeps the Toggle on the parent flat and the
// progress count honest.
//
// Pure module: no I/O.

import { isClosedStatus, isDroppedStatus, type StatusCatalog } from './taskStatus';

export const SUBTASK_OF_RELATION = 'subtask_of';

export interface SubtaskLink {
  childId: string;
  parentId: string;
}

export interface SubtaskRelationRow {
  id?: string;
  source_id: string;
  target_id: string;
  relation_type: string;
}

/** Keeps only the subtask_of rows and reads them as links. */
export function linksFromRelations(rows: SubtaskRelationRow[]): SubtaskLink[] {
  return rows
    .filter((r) => r.relation_type === SUBTASK_OF_RELATION)
    .map((r) => ({ childId: r.source_id, parentId: r.target_id }));
}

export type SubtaskCheck = { ok: true } | { ok: false; error: string };

/** Run before writing a link. */
export function canAddSubtask(links: SubtaskLink[], parentId: string, childId: string): SubtaskCheck {
  if (parentId === childId) return { ok: false, error: 'משימה לא יכולה להיות תת-משימה של עצמה' };
  if (links.some((l) => l.childId === parentId)) {
    return { ok: false, error: 'תת-משימה לא יכולה להכיל תת-משימות: המערכת מאפשרת רמה אחת' };
  }
  if (links.some((l) => l.parentId === childId)) {
    return { ok: false, error: 'למשימה הזאת יש תת-משימות, ולכן היא לא יכולה להפוך לתת-משימה' };
  }
  if (links.some((l) => l.childId === childId)) {
    return { ok: false, error: 'המשימה כבר תת-משימה של משימה אחרת' };
  }
  return { ok: true };
}

export interface SubtaskProgress {
  done: number;
  total: number;
}

/** Cancelled / not-relevant / duplicate subtasks leave the count: work that was
 *  dropped is neither done nor outstanding, so it must not hold the parent at
 *  "3 of 4" forever. */
export function subtaskProgress(
  catalog: StatusCatalog,
  statuses: (string | null | undefined)[],
): SubtaskProgress {
  let done = 0;
  let total = 0;
  for (const s of statuses) {
    if (isDroppedStatus(catalog, s)) continue;
    total += 1;
    if (isClosedStatus(catalog, s)) done += 1;
  }
  return { done, total };
}
